/**
 * Browser-based geolocation provider using the Web Geolocation API.
 * 
 * ARCHITECTURAL OVERVIEW:
 * Wraps the browser's navigator.geolocation API to provide a clean, testable
 * interface for geolocation services. This class isolates the browser-specific
 * code from the business logic in GeolocationService.
 * 
 * DEPENDENCY INJECTION:
 * Accepts navigator object as constructor parameter to enable testing and
 * support different browser environments.
 * 
 * REFERENTIAL TRANSPARENCY:
 * The class methods are thin wrappers around the browser API. Side effects
 * (geolocation calls) are isolated at this boundary, making higher-level
 * code more testable and predictable.
 * 
 * @module services/providers/BrowserGeolocationProvider
 * @since 0.6.1-alpha
 * @author Marcelo Pereira Barbosa
 */

import GeolocationProvider from './GeolocationProvider.js';

/**
 * Browser geolocation provider using navigator.geolocation API.
 * 
 * @class BrowserGeolocationProvider
 * @extends GeolocationProvider
 */
class BrowserGeolocationProvider extends GeolocationProvider {
	navigator: object | null;

	/**
	 * Creates a new BrowserGeolocationProvider instance.
	 * 
	 * DEPENDENCY INJECTION:
	 * Accepts navigator object to enable testing with mock objects and
	 * support different execution environments (browser, Node.js).
	 * 
	 * NULL HANDLING:
	 * - No argument: Uses global navigator if available, else null
	 * - Explicit undefined/null: Stores as-is (no fallback to global)
	 * 
	 * @param {Object} [navigatorObj] - Navigator object (injectable for testing)
	 * 
	 * @example
	 * // Browser usage - uses global navigator
	 * const provider = new BrowserGeolocationProvider();
	 * 
	 * @example
	 * // Testing with mock
	 * const mockNavigator = { geolocation: { getCurrentPosition: jest.fn() } };
	 * const provider = new BrowserGeolocationProvider(mockNavigator);
	 * 
	 * @example
	 * // Explicit null - no navigator, no fallback
	 * const provider = new BrowserGeolocationProvider(null);
	 */
	constructor(navigatorObj?: object | null) {
		super();
		
		// Distinguish between "no argument" and "explicit undefined/null"
		// Using arguments.length to detect if argument was provided
		if (arguments.length === 0) {
			// No argument provided - use global navigator fallback
			this.navigator = (typeof navigator !== 'undefined' ? navigator : null);
		} else {
			// Argument provided (even if undefined/null) - use it directly, no fallback
			this.navigator = navigatorObj === undefined ? null : navigatorObj;
		}
	}

	/**
	 * Gets the current geographic position.
	 * 
	 * Delegates to navigator.geolocation.getCurrentPosition with all parameters.
	 * 
	 * @param {Function} successCallback - Called with position on success
	 * @param {Function} errorCallback - Called with error on failure
	 * @param {Object} options - Geolocation options
	 * @returns {void}
	 */
	getCurrentPosition(successCallback: (pos: object) => void, errorCallback: (err: object) => void, options: object): void {
		if (!this.isSupported()) {
			const error = {
				code: 0,
				message: 'Geolocation is not supported'
			};
			if (errorCallback) {
				errorCallback(error);
			}
			return;
		}

		this.navigator.geolocation.getCurrentPosition(
			successCallback,
			errorCallback,
			options
		);
	}

	/**
	 * Starts watching the geographic position for changes.
	 * 
	 * Delegates to navigator.geolocation.watchPosition with all parameters.
	 * 
	 * @param {Function} successCallback - Called with position on each update
	 * @param {Function} errorCallback - Called with error on failure
	 * @param {Object} options - Geolocation options
	 * @returns {number|null} Watch ID for clearing the watch, or null if not supported
	 */
	watchPosition(successCallback: (pos: object) => void, errorCallback: (err: object) => void, options: object): number {
		if (!this.isSupported()) {
			return null;
		}

		return this.navigator.geolocation.watchPosition(
			successCallback,
			errorCallback,
			options
		);
	}

	/**
	 * Stops watching the geographic position.
	 * 
	 * Delegates to navigator.geolocation.clearWatch.
	 * 
	 * @param {number} watchId - Watch ID returned from watchPosition
	 * @returns {void}
	 */
	clearWatch(watchId: number): void {
		if (this.isSupported() && watchId !== null) {
			this.navigator.geolocation.clearWatch(watchId);
		}
	}

	/**
	 * Checks if geolocation is supported by the browser.
	 * 
	 * PURE FUNCTION:
	 * Deterministic check based on navigator object structure.
	 * No side effects, always returns same result for same navigator object.
	 * 
	 * @returns {boolean} True if geolocation is supported
	 */
	isSupported(): boolean {
		return Boolean(this.navigator && 'geolocation' in this.navigator);
	}

	/**
	 * Checks if Permissions API is supported by the browser.
	 * 
	 * PURE FUNCTION:
	 * Deterministic check based on navigator object structure.
	 * 
	 * @returns {boolean} True if Permissions API is supported
	 */
	isPermissionsAPISupported(): boolean {
		return Boolean(this.navigator && 'permissions' in this.navigator);
	}

	/**
	 * Gets the navigator object for advanced use cases.
	 * 
	 * @returns {Object|null} Navigator object or null if not available
	 */
	getNavigator(): object | null {
		return this.navigator;
	}
}

export default BrowserGeolocationProvider;
/**
 * Module exports for browser geolocation provider.
 * @exports BrowserGeolocationProvider - Native browser Geolocation API implementation
 */
export { BrowserGeolocationProvider };
